import Alert from '@mui/material/Alert';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

import Add from '@mui/icons-material/Add';
import Clear from '@mui/icons-material/Clear';
import Remove from '@mui/icons-material/Remove';
import ShoppingCart from '@mui/icons-material/ShoppingCart';
import QrCodeScanner from '@mui/icons-material/QrCodeScanner';

import { useState, useEffect, useRef } from 'react';

import jsPDF from 'jspdf';

import * as api from '../../api.js';
import { useAuth } from '../../AuthContext';

export const POS = () => {
    const [ cart, setCart ] = useState([]);
    const [ scannedItem, setScannedItem ] = useState('');
    const [ loading, setLoading ] = useState(false);
    const [ error, setError ] = useState(null);
    const [ success, setSuccess ] = useState(null);
    const [ paymentError, setPaymentError ] = useState(null);
    const [ paymentMethod, setPaymentMethod ] = useState('card');
    const [ cashReceived, setCashReceived ] = useState('');
    const [ cardLastFour, setCardLastFour ] = useState('');
    const [ checkNumber, setCheckNumber ] = useState('');
    const [ allItems, setAllItems ] = useState([]);
    const [ searchResults, setSearchResults ] = useState([]);
    const scanInputRef = useRef(null);

    const { token, claims } = useAuth();

    // Focus scan input on mount
    useEffect(() => {
        if (scanInputRef.current) {
            scanInputRef.current.focus();
        }
    }, []);

    // Load all items for search
    useEffect(() => {
        const loadAllItems = async () => {
            try {
                const items = await api.getAllItems(token);
                if (Array.isArray(items)) {
                    setAllItems(items);
                }
            } catch (err) {
                console.error('Failed to load items for search:', err);
            }
        };

        if (token) {
            loadAllItems();
        }
    }, [ token ]);

    // Search items by id and barcode
    const searchItems = (query) => {
        if (!query.trim()) {
            setSearchResults([]);
            return;
        }

        const filtered = allItems.filter(item => {
            const itemId = item.id?.toString().toLowerCase() || '';
            const barcode = item.barcode?.toString().toLowerCase() || '';
            const searchTerm = query.toLowerCase();

            return itemId.includes(searchTerm) || barcode.includes(searchTerm);
        }).slice(0, 10); // Limit to 10 results

        setSearchResults(filtered);
    };

    // Check if user is admin
    if (!claims?.admin) {
        return (
            <Container maxWidth="md" sx={ { mt: 4 } }>
                <Alert severity="error">
                    Access denied. POS is only available to administrators.
                </Alert>
            </Container>
        );
    }

    const handleScan = async (itemId) => {
        if (!itemId.trim()) return;

        try {
            setLoading(true);
            setError(null);

            const item = await api.getItem(itemId, token);

            if (!item) {
                setError('Item not found');
                return;
            }

            if (item.sold || (Number(item.quantity) || 0) <= 0) {
                setError('Item has been sold or quantity is 0.');
                return;
            }

            // Check if item is already in cart
            const existingItemIndex = cart.findIndex(cartItem => cartItem.id === item.id);

            if (existingItemIndex >= 0) {
                // Update quantity if already in cart
                const updatedCart = [ ...cart ];

                if (updatedCart[ existingItemIndex ].quantity < (item.quantity || 1)) {
                    updatedCart[ existingItemIndex ].quantity += 1;

                    setCart(updatedCart);
                    setSuccess(`Added another ${ item.name } to cart`);
                } else {
                    setError('Maximum quantity reached for this item');
                }
            } else {
                // Add new item to cart
                setCart([ ...cart, {
                    ...item,
                    cartQuantity: 1
                } ]);

                setSuccess(`Added ${ item.name } to cart`);
            }

            setScannedItem('');
            setSearchResults([]);

        } catch (err) {
            setError('Failed to scan item');
        } finally {
            setLoading(false);
        }
    };

    const handleScanSubmit = (e) => {
        e.preventDefault();
        setSearchResults([]);
        handleScan(scannedItem);
    };

    const updateCartQuantity = (itemId, newQuantity) => {
        if (newQuantity <= 0) {
            removeFromCart(itemId);

            return;
        }

        setCart(cart.map(item =>
            item.id === itemId
                ? { ...item, cartQuantity: Math.min(newQuantity, item.quantity || 1) }
                : item
        ));
    };

    const removeFromCart = (itemId) => {
        setCart(cart.filter(item => item.id !== itemId));
    };

    const getTotal = () => {
        return cart.reduce((total, item) => total + (item.price * item.cartQuantity), 0);
    };

    const handlePayment = async () => {
        if (cart.length === 0) {
            setError('Cart is empty');

            return;
        }

        try {
            setLoading(true);
            setError(null);
            setPaymentError(null);

            const saleData = {
                items: cart.map(item => ({
                    id: item.id,
                    quantity: item.cartQuantity,
                    price: item.price,
                    name: item.name
                })),
                paymentMethod,
                total: getTotal()
            };

            if (paymentMethod === 'card') {
                if (!cardLastFour || cardLastFour.length !== 4) {
                    setPaymentError('Please enter the last 4 digits of the card');

                    return;
                }

                saleData.cardLastFour = cardLastFour;
            } else if (paymentMethod === 'cash') {
                const received = parseFloat(cashReceived);

                if (isNaN(received) || received < getTotal()) {
                    setPaymentError('Invalid cash amount received');

                    return;
                }
                saleData.cashReceived = received;
            } else if (paymentMethod === 'check') {
                if (!checkNumber) {
                    setPaymentError('Please enter the check number');

                    return;
                }
                saleData.checkNumber = checkNumber;
            }

            await api.processSale(saleData, token);

            setSuccess('Sale completed successfully!');
            generateReceipt();
            setCart([]);
            setCashReceived('');
            setCardLastFour('');
            setCheckNumber('');

        } catch (err) {
            setPaymentError(err.message || 'Payment failed');
        } finally {
            setLoading(false);
        }
    };

    const clearCart = () => {
        setCart([]);
        setSuccess(null);
        setError(null);
        setPaymentError(null);
    };

    const generateReceipt = () => {
        if (cart.length === 0) {
            setError('No items in cart to generate receipt');
            return;
        }

        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'pt',
            format: [ 216, 400 ] // 3" width (216pt), initial height 400pt, will auto-expand
        });

        const pageWidth = 216;
        const margin = 5;
        let yPosition = 20;

        // Use fixed-width font
        doc.setFont('courier', 'normal');

        // Header
        doc.setFontSize(12);
        doc.setFont('courier', 'bold');
        doc.text('PATROL360 POS', margin, yPosition);
        yPosition += 15;

        doc.setFontSize(8);
        doc.setFont('courier', 'normal');
        doc.text('Ski Patrol Equipment Swap', margin, yPosition);
        yPosition += 10;

        // Date/Time
        const now = new Date();
        const dateTime = now.toLocaleString();
        doc.text(dateTime, margin, yPosition);
        yPosition += 15;

        // Separator line
        doc.line(margin, yPosition, pageWidth - margin, yPosition);
        yPosition += 10;

        // Items
        doc.setFontSize(8);

        cart.forEach(item => {
            const itemTotal = item.price * item.cartQuantity;

            // Item name with brand
            const fullItemName = `${ item.name } ${ item.brand } ${ item.desc?.substring(0, 25) }`;
            const itemName = fullItemName.length > 1500 ? fullItemName.substring(0, 147) + '...' : fullItemName;
            doc.setFont('courier', 'bold');
            doc.text(itemName, margin, yPosition);
            yPosition += 10;

            // Sublines for qty, price, total
            doc.setFont('courier', 'normal');
            doc.text(`Qty: ${ item.cartQuantity } @ ${ item.price.toFixed(2) } each`, margin + 5, yPosition);
            doc.text(`Total: $${ itemTotal.toFixed(2) }`, margin + 100, yPosition);
            yPosition += 15;

            // Check if we need a new page
            if (yPosition > 350) {
                doc.addPage();
                yPosition = 20;
            }
        });

        // Separator line
        doc.line(margin, yPosition, pageWidth - margin, yPosition);
        yPosition += 8;

        // Total
        doc.setFont('courier', 'bold');
        doc.setFontSize(10);
        const total = getTotal();
        doc.text(`TOTAL: $${ total.toFixed(2) }`, margin, yPosition);
        yPosition += 30;

        // Payment method
        doc.setFont('courier', 'normal');
        doc.setFontSize(8);

        let paymentText = '';

        if (paymentMethod === 'card') {
            paymentText = `Card (****${ cardLastFour })`;
        } else if (paymentMethod === 'cash') {
            paymentText = `Cash (Received: $${ parseFloat(cashReceived).toFixed(2) })`;
        } else if (paymentMethod === 'check') {
            paymentText = `Check (#${ checkNumber })`;
        }

        doc.text(`Payment: ${ paymentText }`, margin, yPosition);
        yPosition += 30;

        // Footer
        doc.setFontSize(8);
        doc.text('Thank you for your business!', margin, yPosition);
        yPosition += 16;
        doc.text('Your patronage helps keep the slopes safe!', margin, yPosition);

        // Save the PDF
        const fileName = `receipt_${ now.getTime() }.pdf`;
        doc.save(fileName);

        setSuccess('Receipt generated successfully!');
    };

    return (
        <Box sx={ { width: '100%' } }>
            <AppBar position="static" color="primary">
                <Toolbar>
                    <ShoppingCart sx={ { mr: 2 } } />

                    <Typography variant="h6" component="div" sx={ { flexGrow: 1 } }>
                        Patrol360 POS
                    </Typography>

                    <Chip
                        label={ `Items: ${ cart.length }` }
                        color="secondary"
                        variant="outlined"
                    />
                </Toolbar>
            </AppBar>

            <Container sx={ { mt: 2, mb: 2, p: 1 } }>
                {/* Messages */ }
                { error && (
                    <Alert variant='filled' severity="error" sx={ { mt: 2 } } onClose={ () => setError(null) }>
                        { error }
                    </Alert>
                ) }

                { success && (
                    <Alert variant='filled' severity="success" sx={ { mt: 2 } } onClose={ () => setSuccess(null) }>
                        { success }
                    </Alert>
                ) }

                <Grid container spacing={ 2 }>
                    {/* Scan Section */ }
                    <Grid size={ { xs: 12, md: 6 } }>
                        <Paper sx={ { p: 2 } }>
                            <Typography variant="h6" gutterBottom>
                                <QrCodeScanner sx={ { mr: 1, verticalAlign: 'middle' } } />
                                Scan Item
                            </Typography>

                            <form onSubmit={ handleScanSubmit }>
                                <TextField
                                    fullWidth
                                    label="Scan barcode or enter item ID"
                                    value={ scannedItem }
                                    onChange={ (e) => {
                                        setScannedItem(e.target.value);
                                        searchItems(e.target.value);
                                    } }
                                    inputRef={ scanInputRef }
                                    autoComplete="off"
                                    sx={ { mb: 2 } }
                                />

                                {/* Search Results */ }
                                { searchResults.length > 0 && (
                                    <Card sx={ { mb: 2, maxHeight: '200px', overflow: 'auto' } }>
                                        <CardContent sx={ { p: 1 } }>
                                            <Typography variant="subtitle2" gutterBottom>
                                                Search Results ({ searchResults.length }{ searchResults.length === 10 ? '+' : '' })
                                            </Typography>

                                            { searchResults.map((item) => (
                                                <Box
                                                    key={ item.id }
                                                    onClick={ item.quantity === 0 ? undefined : () => handleScan(item.id) }
                                                    sx={ {
                                                        p: 1,
                                                        cursor: item.quantity === 0 ? 'not-allowed' : 'pointer',
                                                        '&:hover': { bgcolor: 'action.hover' },
                                                        borderRadius: 1,
                                                        mb: 0.5
                                                    } }
                                                >
                                                    <Typography variant="body2" sx={ { fontWeight: 'medium' } }>
                                                        { item.name }
                                                    </Typography>

                                                    <Typography variant="caption" color="text.secondary">
                                                        ID: { item.id } { item.barcode ? `| Barcode: ${ item.barcode }` : '' } | ${ item.price ? `${ item.price.toFixed(2) }` : 'Price not set' } | Qty: { item.quantity || 0 }
                                                    </Typography>
                                                </Box>
                                            )) }

                                            { searchResults.length === 10 && allItems.filter(item => {
                                                const itemId = item.id?.toString().toLowerCase() || '';
                                                const barcode = item.barcode?.toString().toLowerCase() || '';
                                                const searchTerm = scannedItem.toLowerCase();

                                                return itemId.includes(searchTerm) || barcode.includes(searchTerm);
                                            }).length > 10 && (
                                                    <Typography variant="caption" color="text.secondary" sx={ { display: 'block', textAlign: 'center', mt: 1 } }>
                                                        more...
                                                    </Typography>
                                                ) }
                                        </CardContent>
                                    </Card>
                                ) }

                                <Button
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    disabled={ loading }
                                >
                                    Add to Cart
                                </Button>
                            </form>
                        </Paper>
                    </Grid>

                    {/* Cart Section */ }
                    <Grid size={ { xs: 12, md: 6 } }>
                        <Paper sx={ { p: 2, height: '400px', overflow: 'auto' } }>
                            <Box sx={ { display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 } }>
                                <Typography variant="h6">Cart</Typography>

                                <Button
                                    size="small"
                                    onClick={ clearCart }
                                    disabled={ cart.length === 0 }
                                >
                                    Clear
                                </Button>
                            </Box>

                            { cart.length === 0 ? (
                                <Typography color="text.secondary" sx={ { textAlign: 'center', mt: 4 } }>
                                    No items in cart
                                </Typography>
                            ) : (
                                cart.map((item) => (
                                    <Card key={ item.id } sx={ { mb: 1 } }>
                                        <CardContent sx={ { py: 1, '&:last-child': { pb: 1 } } }>
                                            <Box sx={ { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } }>
                                                <Box sx={ { display: 'flex', alignItems: 'center', gap: 2, flex: 1 } }>
                                                    { item.image && (
                                                        <Avatar
                                                            src={ item.imageUrl }
                                                            alt={ item.name }
                                                            variant="rounded"
                                                            sx={ { width: 48, height: 48 } }
                                                        />
                                                    ) }

                                                    <Box sx={ { flex: 1 } }>
                                                        <Typography variant="body1" sx={ { fontWeight: 'bold' } }>
                                                            { item.name }
                                                        </Typography>

                                                        <Typography variant="body2" color="text.secondary">
                                                            ${ item.price.toFixed(2) } each
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Box sx={ { display: 'flex', alignItems: 'center', gap: 1 } }>
                                                    <IconButton
                                                        size="small"
                                                        onClick={ () => updateCartQuantity(item.id, item.cartQuantity - 1) }
                                                    >
                                                        <Remove fontSize="small" />
                                                    </IconButton>

                                                    <Typography sx={ { minWidth: '30px', textAlign: 'center' } }>
                                                        { item.cartQuantity }
                                                    </Typography>

                                                    <IconButton
                                                        size="small"
                                                        onClick={ () => updateCartQuantity(item.id, item.cartQuantity + 1) }
                                                        disabled={ item.cartQuantity >= (item.quantity || 1) }
                                                    >
                                                        <Add fontSize="small" />
                                                    </IconButton>

                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={ () => removeFromCart(item.id) }
                                                    >
                                                        <Clear fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                ))
                            ) }
                        </Paper>
                    </Grid>

                    {/* Payment Section */ }
                    <Grid size={ { xs: 12 } }>
                        <Paper sx={ { p: 2 } }>
                            <Typography variant="h6" gutterBottom>
                                Payment
                            </Typography>

                            <Grid container spacing={ 2 }>
                                <Grid size={ { xs: 12, sm: 6 } }>
                                    <FormControl component="fieldset">
                                        <FormLabel component="legend">Payment Method</FormLabel>
                                        <RadioGroup
                                            row
                                            value={ paymentMethod }
                                            onChange={ (e) => setPaymentMethod(e.target.value) }
                                        >
                                            <FormControlLabel value="card" control={ <Radio /> } label="Card" />
                                            <FormControlLabel value="cash" control={ <Radio /> } label="Cash" />
                                            <FormControlLabel value="check" control={ <Radio /> } label="Check" />
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>

                                { paymentMethod === 'cash' && (
                                    <Grid size={ { xs: 12, sm: 6 } }>
                                        <TextField
                                            fullWidth
                                            required
                                            label="Cash Received"
                                            type="number"
                                            step="0.01"
                                            value={ cashReceived }
                                            onChange={ (e) => setCashReceived(e.target.value) }
                                            placeholder="0.00"
                                        />
                                    </Grid>
                                ) }

                                { paymentMethod === 'card' && (
                                    <Grid size={ { xs: 12, sm: 6 } }>
                                        <TextField
                                            fullWidth
                                            required
                                            label="Card Last 4 Digits"
                                            value={ cardLastFour }
                                            onChange={ (e) => setCardLastFour(e.target.value.replace(/\D/g, '').slice(0, 4)) }
                                            placeholder="1234"
                                            slotProps={ { htmlInput: { maxLength: 4 } } }
                                        />
                                    </Grid>
                                ) }

                                { paymentMethod === 'check' && (
                                    <Grid size={ { xs: 12, sm: 6 } }>
                                        <TextField
                                            fullWidth
                                            required
                                            label="Check Number"
                                            value={ checkNumber }
                                            onChange={ (e) => setCheckNumber(e.target.value) }
                                            placeholder="Check #"
                                            slotProps={ { htmlInput: { minLength: 3 } } }
                                        />
                                    </Grid>
                                ) }
                            </Grid>

                            <Divider sx={ { my: 2 } } />

                            <Box sx={ { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } }>
                                <Typography variant="h5">
                                    Total: ${ getTotal().toFixed(2) }
                                </Typography>

                                <Box sx={ { display: 'flex', gap: 1 } }>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={ generateReceipt }
                                        disabled={ cart.length === 0 }
                                    >
                                        Print Receipt
                                    </Button>

                                    <Button
                                        variant="contained"
                                        color="success"
                                        size="large"
                                        onClick={ handlePayment }
                                        disabled={ loading || cart.length === 0 }
                                    >
                                        { loading ? 'Processing...' : 'Complete Sale' }
                                    </Button>
                                </Box>
                            </Box>

                            { paymentError && (
                                <Alert variant='filled' severity="error" sx={ { mt: 2 } }>
                                    { paymentError }
                                </Alert>
                            ) }

                            { paymentMethod === 'cash' && cashReceived && (
                                <Typography variant="body2" color="success.main" sx={ { mt: 1 } }>
                                    Change: ${ (parseFloat(cashReceived) - getTotal()).toFixed(2) }
                                </Typography>
                            ) }
                        </Paper>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};